import { useEffect, useRef, useState, Fragment } from "react";
import { findSubNode, getCustomisationValue, getGroupData } from "../../EditablePreview";
import VisualisedNode from "../VisualisedNode";
import { COLOUR_NAME_TO_COLOUR_HEX } from "../../../../../../../helpers/Colours";
import LiveTextBox from "./live-text-box";
import { NODE_LIST_CONTROLLER } from "../../../../../../NodeList/NodeList";

const lastLiveTextSelections = {
    templateIndex: 0,
    presetTextIndex: -1,
    presetTextNode: null,
};

const LiveText = (props) => {
    // console.log('LiveText props', props);
    const previewScale = props.previewScale || 1;
    const campaignData = props.campaignData;

    const [forceRerender, setForceRerender] = useState(0);
    const forceRerenderNow = () => {
        setForceRerender(forceRerender + 1);
    }

    let liveTextNode = props.node;
    if (liveTextNode.path.split('.')[1] !== 'Main_View') {
        // console.warn('LiveText component being used on a node that is not a Live Text Main View - this may cause issues.');
        // Try to find the Live Text Main View node in the sub nodes
        for (const subNode of props.node.subNodes) {
            if (subNode.path.split('.')[1] === 'Main_View') {
                liveTextNode = subNode;
                break;
            }
        }
    }

    const customisationVars = liveTextNode.customisationVars;

    const getTemplateDataFromCustomisationData = (templateIndex) => {
        const templateData = customisationVars.setup.templates.settings[templateIndex];
        // console.log('getTemplateDataFromCustomisationData:', templateData);
        return templateData;
    }

    let templateIndex = lastLiveTextSelections.templateIndex;
    if (typeof props.activeNode?.dataIndex !== 'undefined' && props.activeNode?.parent?.name?.toLowerCase?.()?.includes('templates')) {
        templateIndex = props.activeNode.dataIndex;   
    }
    if (templateIndex >= getGroupData(customisationVars, 'setup.templates.settings', -1).length) {
        templateIndex = getGroupData(customisationVars, 'setup.templates.settings', -1).length - 1;
    }
    if (templateIndex !== lastLiveTextSelections.templateIndex) {
        lastLiveTextSelections.templateIndex = templateIndex;
        // console.log('LiveText Setting up new template index', templateIndex);
    }

    const getPresetTextDataFromCustomisationData = (presetIndex) => {
        const presetTextData = customisationVars.setup.presetText.presets[presetIndex];
        return presetTextData;
    }

    let presetTextIndex = lastLiveTextSelections.presetTextIndex;
    if (typeof props.activeNode?.dataIndex !== 'undefined' && props.activeNode?.parent?.name?.toLowerCase?.()?.includes('text presets')) {
        presetTextIndex = props.activeNode.dataIndex;
        lastLiveTextSelections.presetTextNode = props.activeNode;
    }
    if (presetTextIndex >= getGroupData(customisationVars, 'setup.presetText.presets', -1).length) {
        presetTextIndex = getGroupData(customisationVars, 'setup.presetText.presets', -1).length - 1;
    }
    if (presetTextIndex !== lastLiveTextSelections.presetTextIndex) {
        lastLiveTextSelections.presetTextIndex = presetTextIndex;
        // console.log('LiveText Setting up new preset text index', presetTextIndex);
    }

    /* if (templateIndex < 0) {
        return null;
    } */

    const templateData = getTemplateDataFromCustomisationData(templateIndex);

    const fullContainerCssVars = {"--xOffset": "0px", "--yOffset": "0px"};
    let position = customisationVars.setup.settings.general[0].globalPosition;
    let positionDataLink = 'setup.settings.general.globalPosition';
    let positionDataIndex = 0;
    let offsetXDataLink = 'setup.settings.general.globalXOffset';
    let offsetYDataLink = 'setup.settings.general.globalYOffset';
    // console.log('--- LiveTextView renderContent:', this.props.activeTextBoxes, customisationData, fullContainerCssVars, position, this.state);
    let absOffsetX = 0;
    let absOffsetY = 0;
    if (customisationVars.setup.settings.general[0].enableResponses || customisationVars.setup.settings.general[0].stackMessages) {
        // console.log('--- Using global position settings');
        // using global position settings
        fullContainerCssVars['--xOffset'] = customisationVars.setup.settings.general[0].globalXOffset * previewScale + 'px';
        fullContainerCssVars['--yOffset'] = customisationVars.setup.settings.general[0].globalYOffset * previewScale + 'px';
        absOffsetX = Math.abs(customisationVars.setup.settings.general[0].globalXOffset * previewScale);
        absOffsetY = Math.abs(customisationVars.setup.settings.general[0].globalYOffset * previewScale);
        position = customisationVars.setup.settings.general[0].globalPosition
    } else {
        // Using individual position settings (there will be only one textbox at a time)
        if (templateData) {
            // console.log('--- Setting pos from template data:', templateData);
            fullContainerCssVars['--xOffset'] = templateData.xOffset * previewScale + 'px';
            fullContainerCssVars['--yOffset'] = templateData.yOffset * previewScale + 'px';
            absOffsetX = Math.abs(templateData.xOffset * previewScale);
            absOffsetY = Math.abs(templateData.yOffset * previewScale);
            position = templateData.position;
            positionDataLink = 'setup.templates.settings.position';
            positionDataIndex = templateIndex;
            offsetXDataLink = 'setup.templates.settings.xOffset';
            offsetYDataLink = 'setup.templates.settings.yOffset';
        }
    }

    const [randomisedPositionObj, setRandomisedPositionObj] = useState(null);
    const generateRandomPosition = (force = false) => {
        if (randomisedPositionObj && !force) {
            return randomisedPositionObj;
        }

        // We need to randomise the position (cool feature)
        const posOptions = ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top-center', 'bottom-center', 'middle-left', 'middle-right', 'middle-center'];
        // Pick a position
        const randomPosIndex = Math.floor(Math.random() * posOptions.length);
        const randomPos = posOptions[randomPosIndex];
        // Some offsets need to be positive, some negative, some can be random
        const xYOffsetSigns = [{x: 1, y: 1}, {x: -1, y: 1}, {x: 1, y: -1}, {x: -1, y: -1}, {x: 0, y: 1}, {x: 0, y: -1}, {x: 1, y: 0}, {x: -1, y: 0}, {x: 0, y: 0}];
        const xOffsetSign = xYOffsetSigns[randomPosIndex].x !== 0 ? xYOffsetSigns[randomPosIndex].x : Math.random() < 0.5 ? 1 : -1;
        const yOffsetSign = xYOffsetSigns[randomPosIndex].y !== 0 ? xYOffsetSigns[randomPosIndex].y : Math.random() < 0.5 ? 1 : -1;
        // Randomly offset 1/4 of the screen (works best with smaller boxes, as wide ones can go off screen)
        absOffsetX += Math.floor(Math.random() * (1920 / 4)) * previewScale;
        absOffsetY += Math.floor(Math.random() * (1080 / 4)) * previewScale;

        // Set the position and offsets
        // position = randomPos;
        // fullContainerCssVars['--xOffset'] = (xOffsetSign * absOffsetX) + 'px';
        // fullContainerCssVars['--yOffset'] = (yOffsetSign * absOffsetY) + 'px';
        const posData = {
            position: randomPos,
            xOffset: xOffsetSign * absOffsetX,
            yOffset: yOffsetSign * absOffsetY,
        };
        setRandomisedPositionObj(posData);

        return posData;
    }
    useEffect(() => {
        if (position !== 'random') {
            setRandomisedPositionObj(null);
        }
    }, [templateIndex, position]);

    if (position === 'random') {
        const posData = generateRandomPosition();

        // Set the position and offsets
        position = posData.position;
        fullContainerCssVars['--xOffset'] = posData.xOffset + 'px';
        fullContainerCssVars['--yOffset'] = posData.yOffset + 'px';
    }

    const overrideVisualisedDataLinks = {
        position: positionDataLink,
        positionDataIndex: positionDataIndex,
        offsetX: offsetXDataLink,
        offsetXDataIndex: positionDataIndex,
        offsetY: offsetYDataLink,
        offsetYDataIndex: positionDataIndex,
    }
    liveTextNode.overrideVisualisedDataLinks = overrideVisualisedDataLinks;

    fullContainerCssVars['--stackSpacing'] = customisationVars.setup.settings.general[0].stackSpacing * previewScale + 'px';
    fullContainerCssVars['--responseSpacing'] = customisationVars.setup.settings.general[0].responseSpacing * previewScale + 'px';
    fullContainerCssVars['--responseIndent'] = customisationVars.setup.settings.general[0].responseIndent * previewScale + 'px';

    /* if (templateData) {
        setTimeout(() => {
            // console.log('--- Setting position:', fullContainerCssVars, position);
            this.setState({fullContainerCssVars: fullContainerCssVars, position: position, positionSet: true});
        }, 10);
    } */

    const presetTextData = getPresetTextDataFromCustomisationData(presetTextIndex);
    const dummyTextBoxSettings = {
        templateIndex: templateIndex,
        headerText: presetTextData?.headerText && presetTextData.headerText.length ? presetTextData.headerText : 'Test Header',
        mainContent: presetTextData?.mainContent && presetTextData.mainContent.length ? presetTextData.mainContent : 'Main content goes here.',
        hide: false,
    }
    if (dummyTextBoxSettings.headerText !== 'Test Header' || dummyTextBoxSettings.mainContent !== 'Main content goes here.') {
        // console.log('Using preset text data for Live Text box:', dummyTextBoxSettings);
        dummyTextBoxSettings.presetTextNode = lastLiveTextSelections.presetTextNode;
    }
    useEffect(() => {
        const checkForTextUpdateInterval = setInterval(() => {
            const presetTextData = getPresetTextDataFromCustomisationData(presetTextIndex);
            if (dummyTextBoxSettings.headerText !== (presetTextData?.headerText && presetTextData.headerText.length ? presetTextData.headerText : 'Test Header') ||
                dummyTextBoxSettings.mainContent !== (presetTextData?.mainContent && presetTextData.mainContent.length ? presetTextData.mainContent : 'Main content goes here.')) 
            {
                // Text has changed, force a re-render
                forceRerenderNow();
            }
        }, 1000);
        return () => {
            clearInterval(checkForTextUpdateInterval);
        }
    }, []);

    // fullContainerCssVars['transform'] = `scale(${previewScale})`;
    // fullContainerCssVars['transformOrigin'] = 'top left';
    fullContainerCssVars['width'] = (1920 * previewScale) + 'px';
    fullContainerCssVars['height'] = (1080 * previewScale) + 'px';

    const generalSettings = customisationVars.setup.settings.general[0];
    const messageStack = [dummyTextBoxSettings];
    if (generalSettings.stackMessages) {
        for (let i = 0; i < 4; i++) {
            messageStack.push(dummyTextBoxSettings);
        }
    }

    return (
         
<span style={fullContainerCssVars}>
                {/* <div className={`live-text-positioner lt-pos-${position}`}> */}
                    <VisualisedNode
                        {...props}
                        topLevel={true}
                        style={{height: 'fit-content', width: 'fit-content'}}
                        node={liveTextNode}
                    >
                        <div style={{...fullContainerCssVars, flexDirection: 'column', display: 'flex', gap: (generalSettings.stackMessages ? (generalSettings.stackSpacing * previewScale) + 'px' : '0px'), width: 'fit-content', height: 'fit-content'}}>
                    {messageStack.map((textBoxSettings, index) => {
                        /* if (index > 0 && textBoxSettings.isResponse && customisationData.setup.settings.general[0].responseStyle === 'side') {
                            return null;
                        }
                        const hasSideResponse = this.props.activeTextBoxes[index + 1] && this.props.activeTextBoxes[index + 1].isResponse && customisationData.setup.settings.general[0].responseStyle === 'side'; */
                        return (
                            <Fragment key={'ltxt_' + index}>
                                <LiveTextBox
                                    {...props}
                                    liveTextNode={liveTextNode}
                                    position={position}
                                    positionDataLink={positionDataLink}
                                    positionDataIndex={positionDataIndex}
                                    offsetXDataLink={offsetXDataLink}
                                    offsetYDataLink={offsetYDataLink}
                                    generalSettings={generalSettings}
                                    addStackingGap={index > 0}

                                    customisationData={customisationVars}
                                    /* templateIndex={this.props.templateIndex}
                                    headerText={this.props.headerText}
                                    mainContent={this.props.mainContent} */
                                    templateIndex={dummyTextBoxSettings.templateIndex/*textBoxSettings.templateIndex*/}
                                    headerText={dummyTextBoxSettings.headerText/*textBoxSettings.headerText*/}
                                    mainContent={dummyTextBoxSettings.mainContent/*textBoxSettings.mainContent*/}
                                    presetTextNode={dummyTextBoxSettings?.presetTextNode ?? null}
                                    presetTextIndex={presetTextIndex}
                                    hide={dummyTextBoxSettings.hide}
                                    textBoxIndex={index}
                                    hideTextBox={() => {}/*this.props.hideTextBox*/}
                                    onTextBoxHidden={() => {}/*this.props.onTextBoxHidden*/}
                                    key={'tb_' + index/*textBoxSettings.id*/}
                                    id={'tb_' + index/*textBoxSettings.id*/}
                                    textBoxSettings={dummyTextBoxSettings}

                                    isSideResponse={false}
                                    hasSideResponse={generalSettings.enableResponses && generalSettings.responseStyle === 'side'/*hasSideResponse*/}
                                    responseData={generalSettings.enableResponses ? dummyTextBoxSettings : null/*hasSideResponse*/ /*? this.props.activeTextBoxes[0 + 1] : null*/}
                                />
                                {generalSettings.enableResponses && generalSettings.responseStyle !== 'side' &&
                                    <LiveTextBox
                                        {...props}
                                        liveTextNode={liveTextNode}
                                        position={position}
                                        positionDataLink={positionDataLink}
                                        positionDataIndex={positionDataIndex}
                                        offsetXDataLink={offsetXDataLink}
                                        offsetYDataLink={offsetYDataLink}
                                        generalSettings={generalSettings}
                                        addStackingGap={true}

                                        customisationData={customisationVars}
                                        /* templateIndex={this.props.templateIndex}
                                        headerText={this.props.headerText}
                                        mainContent={this.props.mainContent} */
                                        templateIndex={dummyTextBoxSettings.templateIndex/*textBoxSettings.templateIndex*/}
                                        headerText={dummyTextBoxSettings.headerText/*textBoxSettings.headerText*/}
                                        mainContent={dummyTextBoxSettings.mainContent/*textBoxSettings.mainContent*/}
                                        presetTextNode={dummyTextBoxSettings?.presetTextNode ?? null}
                                        presetTextIndex={presetTextIndex}
                                        hide={dummyTextBoxSettings.hide}
                                        textBoxIndex={index}
                                        hideTextBox={() => {}/*this.props.hideTextBox*/}
                                        onTextBoxHidden={() => {}/*this.props.onTextBoxHidden*/}
                                        key={'rtb_' + index/*textBoxSettings.id*/}
                                        id={'rtb_' + index/*textBoxSettings.id*/}
                                        textBoxSettings={dummyTextBoxSettings}

                                        isResponse={true}
                                        isSideResponse={false}
                                        hasSideResponse={false}
                                        responseData={null}
                                    />
                                }
                            </Fragment>
                        );
                    })}
                    </div>
                    </VisualisedNode>
                {/* </div> */}
            
        </span>
    );
}

export default LiveText;
